import { Injectable } from '@angular/core';
import { SharedAuthSessionOptions } from './share-auth-session.types';

const TOKEN_REFRESH_THRESHOLD_MSEC = 10 * 60 * 1000;
const TOKEN_REFRESH_BLOCKING_THRESHOLD_MSEC = 1 * 60 * 1000;

export interface SharedAuthSessionAdapter {
  hasStoredSession(): boolean;
  getMsUntilExpiry(): number | null;
  refreshSession(isSilentRefresh: boolean): Promise<boolean>;
}

@Injectable({
  providedIn: 'root',
})
export class SharedAuthSessionService {
  private refreshInFlight: Promise<boolean> | null = null;

  public async ensureValidSession(
    adapter: SharedAuthSessionAdapter,
    options: SharedAuthSessionOptions = {},
  ): Promise<boolean> {
    if (!adapter.hasStoredSession()) {
      return false;
    }

    if (this.refreshInFlight) {
      if (this.isSessionExpired(adapter)) {
        return await this.refreshInFlight;
      }

      return true;
    }

    const msUntilExpiry = adapter.getMsUntilExpiry();
    if (msUntilExpiry == null) {
      return false;
    }

    if (msUntilExpiry > TOKEN_REFRESH_THRESHOLD_MSEC) {
      return true;
    }

    this.refreshInFlight = adapter.refreshSession(options.isSilentRefresh ?? true)
      .finally(() => {
        this.refreshInFlight = null;
      });

    if (
      msUntilExpiry <= TOKEN_REFRESH_BLOCKING_THRESHOLD_MSEC
      || !options.allowBackgroundRefresh
    ) {
      return await this.refreshInFlight;
    }

    return true;
  }

  private isSessionExpired(adapter: SharedAuthSessionAdapter): boolean {
    const msUntilExpiry = adapter.getMsUntilExpiry();
    return msUntilExpiry == null || msUntilExpiry <= 0;
  }
}
